import type { BinMaterial, MaterialCertificate, SiteWithLocations } from '../types';
import { binMaterialLabel } from './siteDrillThrough';

/** A certificate this close to its expiry is flagged so it can be renewed before it lapses. */
export const EXPIRING_SOON_DAYS = 30;

export const CERTIFICATE_DESCRIPTION_MAX_LENGTH = 500;
export const CERTIFICATE_REFERENCE_MAX_LENGTH = 80;
export const MAX_BINS_PER_UPLOAD = 25;
/** Same as the server and vendor documents. */
export const CERTIFICATE_MAX_FILE_BYTES = 25 * 1024 * 1024;

export type CertificateStatus = 'expired' | 'expiring' | 'current' | 'no-expiry';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Today in the viewer's time zone, as YYYY-MM-DD: the calendar the issue and expiry dates use. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function dayNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
}

/**
 * "Sep 1, 2026". Built by hand rather than with Intl so it reads the same on every Node/ICU
 * version, and so a calendar date is never shifted a day by a time zone.
 */
export function formatCertificateDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d || m > 12) return iso;
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** A certificate is still good on its expiry date and expired the day after. */
export function certificateStatus(certificate: Pick<MaterialCertificate, 'expiresAt'>, today: string): CertificateStatus {
  if (!certificate.expiresAt) return 'no-expiry';
  const daysLeft = dayNumber(certificate.expiresAt) - dayNumber(today);
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= EXPIRING_SOON_DAYS) return 'expiring';
  return 'current';
}

export function certificateStatusLabel(certificate: Pick<MaterialCertificate, 'expiresAt'>, today: string): string {
  const status = certificateStatus(certificate, today);
  if (status === 'no-expiry') return 'No expiry';
  const date = formatCertificateDate(certificate.expiresAt as string);
  if (status === 'expired') return `Expired ${date}`;
  return `Expires ${date}`;
}

export function certificatesByBin(certificates: MaterialCertificate[]): Map<string, MaterialCertificate[]> {
  const result = new Map<string, MaterialCertificate[]>();
  for (const certificate of certificates) {
    const list = result.get(certificate.binId) ?? [];
    list.push(certificate);
    result.set(certificate.binId, list);
  }
  return result;
}

/** A bin a certificate can go on: it holds a material, which is what gets certified. */
export interface CertificateBinChoice {
  id: string;
  /** "A-2 Winter sand pile · Bin 3". */
  label: string;
  materialId: string;
  materialLabel: string;
}

/** Every bin at the site holding a material, in location then bin order. */
export function certifiableBins(site: Pick<SiteWithLocations, 'locations'>): CertificateBinChoice[] {
  return site.locations.flatMap((location) =>
    location.bins
      .filter((bin) => bin.material)
      .map((bin) => ({
        id: bin.id,
        label: `${location.name} · ${bin.code}`,
        materialId: (bin.material as BinMaterial).id,
        materialLabel: binMaterialLabel(bin) ?? (bin.material as BinMaterial).id,
      }))
  );
}

export interface CertificateForm {
  file: File | null;
  binIds: string[];
  description: string;
  reference: string;
  issuedAt: string;
}

export type CertificateFormErrors = Partial<Record<'file' | 'binIds' | 'description' | 'reference' | 'issuedAt', string>>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** The same rules the server applies, so the form can say what is wrong before it posts. */
export function validateCertificateForm(form: CertificateForm): CertificateFormErrors {
  const errors: CertificateFormErrors = {};
  if (!form.file) errors.file = 'Attach the certificate file.';
  else if (form.file.size > CERTIFICATE_MAX_FILE_BYTES) errors.file = 'The file is larger than 25 MB.';
  if (form.binIds.length === 0) errors.binIds = 'Choose at least one bin.';
  else if (form.binIds.length > MAX_BINS_PER_UPLOAD) errors.binIds = `Attach to at most ${MAX_BINS_PER_UPLOAD} bins at once.`;
  const description = form.description.trim();
  if (!description) errors.description = 'Describe the certificate, e.g. Sieve analysis - crushing run 14.';
  else if (description.length > CERTIFICATE_DESCRIPTION_MAX_LENGTH) {
    errors.description = `At most ${CERTIFICATE_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  if (form.reference.trim().length > CERTIFICATE_REFERENCE_MAX_LENGTH) {
    errors.reference = `At most ${CERTIFICATE_REFERENCE_MAX_LENGTH} characters.`;
  }
  if (!DATE_ONLY.test(form.issuedAt)) errors.issuedAt = 'Enter the date on the certificate.';
  return errors;
}

/** First line of a description, for headings and messages. */
export function certificateHeadline(description: string): string {
  return description.split('\n')[0].trim();
}

/** "Attached Sieve analysis to A-2 Winter sand pile · Bin 3." / "... to 3 bins." */
export function attachedMessage(description: string, binLabels: string[]): string {
  const where = binLabels.length === 1 ? binLabels[0] : `${binLabels.length} bins`;
  return `Attached ${certificateHeadline(description)} to ${where}.`;
}
